import { Sentry, isSentryEnabled } from '@/configs/sentry.config';

/**
 * The admin panel's logger. Root CLAUDE.md §10 bans `console.log` in committed code; this is
 * the sanctioned alternative. Breadcrumbs attach to whatever error is reported next, which is
 * usually the only way to reconstruct what an admin was doing when a page broke.
 */
export const logBreadcrumb = (message: string, data?: Record<string, unknown>): void => {
  if (isSentryEnabled()) {
    Sentry.addBreadcrumb({ message, data, level: 'info' });
  }

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info(`[shoutt] ${message}`, data ?? '');
  }
};

/** Reports a caught error that the UI recovered from. */
export const recordHandledError = (error: unknown, context?: Record<string, unknown>): void => {
  const normalised = error instanceof Error ? error : new Error(String(error));

  if (isSentryEnabled()) {
    // Context goes on a scope local to this event — setting it globally would make a one-off
    // detail stick to every later report.
    Sentry.withScope((scope) => {
      if (context) scope.setContext('detail', context);
      Sentry.captureException(normalised);
    });
  }

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.error(`[shoutt] ${normalised.message}`, context ?? '', normalised);
  }
};

/**
 * Attaches the signed-in admin to every subsequent event. Without it reports are anonymous and
 * "is this breaking for one admin or all of them" is unanswerable. `sendDefaultPii` stays off, so
 * only the id and role are attached — deliberately no email.
 */
export const identifyUser = (user: { id: string; role: string } | null): void => {
  if (!isSentryEnabled()) return;
  Sentry.setUser(user ? { id: user.id, role: user.role } : null);
};
